/**
 * Keyword-heuristic kind classifier — stands in for an LLM call.
 * Matches simple keywords to help users pick the right object kind
 * when creating a new design-change proposal. See Atlas v2 mockup
 * screen 8 for context.
 */

import type { SandboxOp } from './sandboxOps.js';

/** The 9 create-* op kinds this guesser can pick from. */
type CreateKind = Extract<SandboxOp['kind'], `create-${string}`>;

/** Keywords that suggest each kind — simple lowercase substring matching. */
const KIND_KEYWORDS: Record<CreateKind, readonly string[]> = {
  'create-entity': [
    'entity',
    'model',
    'object',
    'record',
    'document',
    'aggregate',
    'resource',
    'persisted',
  ],
  'create-domain': [
    'domain',
    'organize',
    'grouping',
    'namespace',
    'layer',
    'module',
    'bounded context',
  ],
  'create-reference': [
    'reference',
    'relationship',
    'link',
    'points to',
    'refers to',
    'foreign key',
    'edge',
    'connection',
  ],
  'create-surface': [
    'surface',
    'card',
    'chart',
    'overlay',
    'chip',
    'hero',
    'list',
    'sheet',
    'banner',
    'grid',
    'display',
    'render',
    'ui',
    'screen',
  ],
  'create-event': [
    'event',
    'published',
    'triggered',
    'emitted',
    'happened',
    'occurred',
    'fired',
    'action',
    'mutation',
    'purchased',
    'completed',
  ],
  'create-cloud-function': [
    'function',
    'callable',
    'http',
    'trigger',
    'scheduled',
    'service',
    'handler',
    'endpoint',
    'processes',
    'workflow',
  ],
  'create-path': [
    'path',
    'firestore',
    'collection',
    'document',
    'retention',
    '/',
    '{',
    '}',
  ],
  'create-tag': [
    'tag',
    'label',
    'mark',
    'category',
    'tier:',
    'compliance',
    'classification',
  ],
  'create-state-machine': [
    'state machine',
    'workflow',
    'state',
    'transition',
    'lifecycle',
    'state machine',
    'fsm',
    'states',
  ],
};

/**
 * Guess the most likely object kind based on description text.
 * Returns null only if description is empty; no clear keyword match
 * falls back to 'create-entity'. Ties also break toward 'create-entity'
 * (it's first in KIND_KEYWORDS, so it wins strict-greater-than comparisons).
 */
export function guessKind(description: string): CreateKind | null {
  const trimmed = description.trim();
  if (trimmed === '') return null;

  const lower = trimmed.toLowerCase();
  let bestKind: CreateKind | null = null;
  let bestScore = 0;

  // Count keyword matches per kind. Higher count = stronger signal.
  for (const [kind, keywords] of Object.entries(KIND_KEYWORDS) as Array<
    [CreateKind, readonly string[]]
  >) {
    let score = 0;
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        // Full substring match is better than substring of a longer word.
        // For phrases like "state machine", count a match if the entire phrase is present.
        score += keyword.includes(' ') ? 2 : 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestKind = kind;
    }
  }

  // No keyword match — default to entity (most common).
  if (bestKind === null) return 'create-entity';

  return bestKind;
}
